import React, { CSSProperties } from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import Link from '@material-ui/core/Link';
import { Coords } from 'google-map-react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
// Components
import { LCAlert } from '../../../../components/alert';
import App from '../../../../components/app';
import ErrorMessage from '../../../../components/error-message';
import EnhancedForm from '../../../../components/form';
import { FormDatas } from '../../../../local-types';
import { TooltipButton } from '../../../../components/tooltip-button';
import MainLoading from '../../../../components/main-loading';
import { Dialog } from '../../../../components/dialog';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import {
    actions as pricingActions,
    selectors as pricingSelectors,
    types as pricingTypes,
} from '../../../../redux/reducers/pricing';
import { selectors as pendingSelectors } from '../../../../redux/reducers/pendingOperations';
import { AreaByID, Tariff } from '../../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../../utils/global-styles';
import MapUtils from '../../../../utils/mapUtils';
import ObjectUtils from '../../../../utils/objectUtils';
import { AccountRight, RightRank } from '../../../../utils/userUtils';
import { paths } from '../../../paths';
import { ActionLogs } from '../../../../components/action-logs';
import { clsx } from '../../../../utils/htmlUtils';
import GoogleMapSelectPolygon from '../../../../components/google-map-select-polygon';

const queryString = require('query-string');

interface PricingAreasAddProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    center: Coords;
    zoom: number;
    areas: AreaByID;
    getCreateOrEditOrDeleteAreaError: ErrorPayload;
    isLoadingAreas: boolean;
    isSavingArea: boolean;
    getArea: (id: string) => void;
    getAreas: () => void;
    createArea: (payload: any) => void;
    editArea: (id: string, payload: any) => void;
    deleteArea: (id: string, parent_id?: string) => void;
}

interface PricingAreasAddState {
    formData: any;
}

class PricingAreasAddPage extends React.Component<PricingAreasAddProps, PricingAreasAddState> {
    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    alertRef: any = React.createRef();

    links = {
        horokilometric: 'hkm',
        flat_rate: 'forfait',
        provision: 'dispo',
    };

    state = {
        formData: {
            polygon: [],
        },
    };

    componentDidMount() {
        const { location } = this.props;
        const { id } = queryString.parse(location.search);
        this.props.getAreas();
        if (id) {
            this.props.getArea(id);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const { areas, location } = props;
        const { parent_id: parentId, id } = queryString.parse(location.search);

        const needUpdate = !state.formData.id || state.formData.id !== id;
        if (areas && needUpdate) {
            let newFormData: any = { ...state.formData };
            const area = areas && areas[id];
            if (area) {
                newFormData = ObjectUtils.deepCopy(area);
            }
            if (parentId) {
                newFormData.parent_id = parentId;
            }
            if (!newFormData.polygon) {
                newFormData.polygon = [];
            }
            return { formData: newFormData };
        }
        return null;
    }

    //=======================================//
    //=============== Handlers ==============//
    //=======================================//

    handlePolygonReset = () => {
        const { formData } = this.state;
        formData.polygon = [];
        this.setState({ formData });
    };

    handleDeleteArea = () => {
        const { id } = queryString.parse(this.props.location.search);
        this.props.deleteArea(id, null);
    };

    //=========================================//
    //=============== Generators ==============//
    //=========================================//

    generateInputs = (): FormDatas[] => {
        const { parent_id: parentId, id } = queryString.parse(this.props.location.search);
        const { t, areas, center, zoom, globalClasses } = this.props;

        const inputs: FormDatas[] = [
            {
                title: t(id || parentId ? 'areas.edit_area' : 'areas.new_area'),
                sectionStyle: { flex: 1 },
                datas: [
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    { id: 'parent_id', label: t('areas.parent_area'), type: 'picker', searchable: true },
                    {
                        id: 'priority',
                        label: t('areas.priority'),
                        type: 'number',
                        inputsAttributs: { step: 1, min: 0 },
                    },
                ],
            },
            {
                title: t('areas.create_area'),
                sectionStyle: { flex: 2 },
                datas: [
                    {
                        id: 'polygones',
                        label: t('areas.polygon'),
                        required: true,
                        customView: (val, handleChange) => {
                            const { formData } = this.state;
                            let parentPolygon;

                            const { parent_id: parentId } = queryString.parse(this.props.location.search);
                            if (parentId && areas) {
                                const parent = areas[parentId];
                                if (parent?.polygon) {
                                    parentPolygon = parent.polygon;
                                }
                            }

                            return (
                                <GoogleMapSelectPolygon
                                    globalClasses={globalClasses}
                                    center={center}
                                    zoom={zoom}
                                    polygon={formData.polygon}
                                    parentPolygon={parentPolygon}
                                    onPolygonChange={(polygon) => {
                                        this.setState({
                                            formData: {
                                                ...formData,
                                                polygon,
                                            },
                                        });
                                    }}
                                />
                            );
                        },
                    },
                ],
            },
        ];

        if (areas) {
            inputs[0].datas.find((d) => d.id === 'parent_id').options = Object.values(areas).map((r) => ({
                id: r.id,
                name: r.name,
            }));
        }

        return inputs;
    };

    onSubmitNewArea = (formData: any, event) => {
        const { id } = queryString.parse(this.props.location.search);
        const { editArea, createArea } = this.props;

        formData.polygon = this.state.formData.polygon;
        if (!formData.parent_id) {
            delete formData.parent_id;
        }
        this.setState({
            formData: formData,
        });

        if (formData.polygon?.length >= 3) {
            this.alertRef.closeAlert();
            if (id) {
                editArea(id, formData);
            } else {
                createArea(formData);
            }
        } else {
            this.alertRef.showAlert();
        }
    };

    renderRemoveArea = (): JSX.Element => {
        const { id, parent_id: parentId } = queryString.parse(this.props.location.search);
        const { t, globalClasses, areas } = this.props;
        const area = areas && areas[id];

        if (parentId || !area) {
            return null;
        }

        return (
            <Dialog
                btnTitle={t('common:validate')}
                description={t('common:deleteParam', {
                    id: `(${id}) ${area?.name ?? ''}`,
                })}
                customButton={
                    <TooltipButton title={t('common:deleteForever')}>
                        <Button
                            startIcon={<Icon>delete_forever</Icon>}
                            variant="contained"
                            className={clsx(globalClasses.roundButton, globalClasses.btnDanger)}
                            color="inherit"
                        >
                            {t('common:delete')}
                        </Button>
                    </TooltipButton>
                }
                onOK={this.handleDeleteArea}
            />
        );
    };

    renderLinks = () => {
        const { id } = queryString.parse(this.props.location.search);
        const { t, globalClasses, areas } = this.props;
        const area = areas && areas[id];
        const zoneStyle = { flexDirection: 'column', flex: 1 } as CSSProperties;

        return (
            (area?.start_pricing?.length > 0 || area?.end_pricing?.length > 0) && (
                <div className={globalClasses.block} style={{ marginRight: 40, flex: 1 }}>
                    <p className={globalClasses.title}>{t('areas.link_tariffs')}</p>
                    <div style={{ display: 'flex' }}>
                        {['start', 'end'].map((type) => (
                            <div key={type} style={zoneStyle}>
                                <u>{t(`areas.${type}_area`)}:</u>
                                {area?.[`${type}_pricing`]?.map((tariff) => (
                                    <p key={tariff.id}>{this.renderLink(tariff)}</p>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )
        );
    };

    renderLink = (tariff: Tariff) => {
        return (
            <Link
                color="secondary"
                component={RouterLink}
                to={`${paths.pricingManagePrices}/${tariff.pricing_set_id}/new_tarif_${
                    this.links[tariff.type]
                }?edit_id=${tariff.id}`}
            >
                {`${tariff.pricing_set ? `${tariff.pricing_set.name} > ` : ''}${tariff.start_area?.name}${
                    tariff.end_area ? ` => ${tariff.end_area.name}` : ''
                }`}
            </Link>
        );
    };

    render() {
        const { t, isLoadingAreas, isSavingArea, getCreateOrEditOrDeleteAreaError, areas } = this.props;
        const { id } = queryString.parse(this.props.location.search);
        const formPayload = this.state.formData;

        return (
            <App needRights={{ name: AccountRight.PRICING_MGMT, mode: RightRank.W }}>
                <ErrorMessage payload={getCreateOrEditOrDeleteAreaError} />
                {isLoadingAreas && <MainLoading />}
                {!isLoadingAreas && formPayload && (
                    <>
                        {this.renderLinks()}
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={`${paths.pricingManageAreas}?id=${id}`}
                            moreView={
                                <LCAlert
                                    onRef={(ref) => (this.alertRef = ref)}
                                    details={t('error.polygon')}
                                    type="error"
                                />
                            }
                            onReset={this.handlePolygonReset}
                            onSubmit={this.onSubmitNewArea}
                            onRemove={this.renderRemoveArea()}
                            loader={isSavingArea}
                            style={{ paddingLeft: 0 }}
                        />
                        <ActionLogs history={areas?.[id]?.history} withValues allValues />
                    </>
                )}
            </App>
        );
    }
}

const PricingAreasAddPageWithTranslation = withGlobalStyles(
    withTranslation(['pricing', 'common'])(PricingAreasAddPage),
);

const mapStateToProps = (state) => ({
    areas: pricingSelectors.areas(state),
    getCreateOrEditOrDeleteAreaError: errorSelectors.getCreateOrEditOrDeleteAreaError(state),
    isLoadingAreas: pendingSelectors.isLoadingAreas(state),
    isSavingArea: pendingSelectors.isPending(state, pricingTypes.CREATE_AREA, pricingTypes.EDIT_AREA),
});

const mapDispatchToProps = {
    getArea: pricingActions.getArea,
    getAreas: pricingActions.getAreas,
    createArea: pricingActions.createArea,
    editArea: pricingActions.editArea,
    deleteArea: pricingActions.deleteArea,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingAreasAddPageWithTranslation);
